import React, { Component } from 'react';
import { motion } from 'framer-motion';
import { PartyPopper, Sparkles, Wine, UtensilsCrossed, Plus } from 'lucide-react';
const spaServices = [{
  title: 'Chair Massage',
  price: '$25',
  description: '15-minute seated massage focusing on neck, shoulders, and back to release tension and stress.'
}, {
  title: 'Hand & Arm Massage',
  price: '$20',
  description: 'Luxurious hand and forearm massage with warm essential oils and gentle pressure techniques.'
}, {
  title: 'Aromatherapy Session',
  price: '$30',
  description: 'Customized essential oil blend with relaxing massage. Choose from lavender, eucalyptus, or citrus.'
}, {
  title: 'Scalp Massage',
  price: '$18',
  description: 'Soothing head and scalp massage to relieve tension headaches and promote calm.'
}, {
  title: 'Couples Massage',
  price: '$50',
  description: 'Side-by-side relaxation experience for two guests with synchronized massage techniques.'
}];
const venueServices = [{
  title: 'Jacuzzi Use',
  duration: '90 min',
  price: '$100'
}, {
  title: 'Space Rental',
  duration: '5 hrs',
  price: '$450',
  description: 'Includes 4 tables, 20 chairs with covers, speaker with microphone, and clean-up. 420-friendly.',
  featured: true
}, {
  title: 'Bartender',
  duration: '2 hrs',
  price: '$50',
  description: 'Alcohol not included.'
}];
const spaAddOns = [{
  title: 'Hot Wax Paraffin',
  price: '$10'
}, {
  title: 'Small Snack Plate',
  price: '$6'
}];
const drinks = [{
  title: 'Cucumber Infused Water',
  price: '$3',
  image: 'https://images.unsplash.com/photo-1523362628745-0c100150b504?q=80&w=600&auto=format&fit=crop'
}, {
  title: 'Herbal Tea Selection',
  price: '$4',
  image: 'https://images.unsplash.com/photo-1597481499750-3e6b22637e12?q=80&w=600&auto=format&fit=crop'
}, {
  title: 'Sparkling Lemonade',
  price: '$5',
  image: 'https://images.unsplash.com/photo-1556679343-c7306c1976bc?q=80&w=600&auto=format&fit=crop'
}];
const food = [{
  title: 'Fresh Fruit Platter',
  price: '$15',
  description: 'Seasonal berries, melon, and tropical fruits beautifully arranged.',
  image: 'https://cdn.magicpatterns.com/uploads/gzB32Yhdpr5aheLxGD3j7S/Fruit.png'
}, {
  title: 'Veggie Crudités',
  price: '$15',
  description: 'Garden vegetables with herb hummus dip.',
  image: 'https://cdn.magicpatterns.com/uploads/w2KdV49gjkwrHy6BShz5kr/Veggies_platter.png'
}, {
  title: 'Cheese & Crackers',
  price: '$14',
  description: 'Artisan cheese selection with whole grain crackers and dried fruits.',
  image: 'https://cdn.magicpatterns.com/uploads/8Ttek3KJ8L9S8eTFrC3pKw/Cheese_and_Crackers.png'
}, {
  title: 'Tea Sandwiches',
  price: '$15',
  description: 'Assorted finger sandwiches with cucumber, cream cheese, and fresh herbs.',
  image: 'https://cdn.magicpatterns.com/uploads/1NriXFGrzSPe9CxvHbHsJB/Tea_Sandwiches.png'
}, {
  title: 'Club Sandwich Platter',
  price: '$35',
  description: 'Feeds 8–10 people.',
  image: 'https://cdn.magicpatterns.com/uploads/iwfNrJuGr5UvJcfdtUDNth/Club_Sand.png'
}];
const fadeUp = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true
  }
};
function SectionHeader({
  icon: Icon,
  title,
  accent = 'lavender'
}: {
  icon: ComponentType<{
    size?: number;
    strokeWidth?: number;
    className?: string;
  }>;
  title: string;
  accent?: 'lavender' | 'rose';
}) {
  const color = accent === 'rose' ? 'text-rose-400' : 'text-lavender-300';
  const line = accent === 'rose' ? 'from-rose-400/30 to-transparent' : 'from-lavender-400/30 to-transparent';
  return <motion.div {...fadeUp} className="flex items-center gap-4 mb-10" data-id="element-172">
      <Icon className={color} size={20} strokeWidth={1.2} data-id="element-173" />
      <h3 className="font-serif text-2xl md:text-3xl text-lavender-50" data-id="element-174">
        {title}
      </h3>
      <div className={`flex-1 h-[1px] bg-gradient-to-r ${line}`} data-id="element-175"></div>
    </motion.div>;
}
export function MassageParty() {
  return <section id="massage-party" className="py-24 md:py-32 relative" data-id="element-176">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-plum-900/40 to-transparent pointer-events-none" data-id="element-177"></div>

      <div className="max-w-6xl mx-auto px-6 md:px-12 relative" data-id="element-178">
        {/* Header */}
        <div className="text-center mb-20" data-id="element-179">
          <motion.span {...fadeUp} className="text-rose-400 uppercase tracking-[0.2em] text-sm mb-4 block" data-id="element-180">
            Afriel Spa
          </motion.span>
          <motion.h2 {...fadeUp} transition={{
          delay: 0.1
        }} className="font-serif text-4xl md:text-6xl text-lavender-50 mb-4" data-id="element-181">
            Massage Party{' '}
            <span className="italic bg-gradient-to-r from-lavender-300 to-rose-400 bg-clip-text text-transparent" data-id="element-182">
              Menu
            </span>
          </motion.h2>
          <p className="text-lavender-100/60 font-light max-w-xl mx-auto" data-id="element-183">
            Host a private, indulgent gathering — relaxation, refreshments, and
            your own dedicated space.
          </p>
        </div>

        {/* Spa & Massage Services */}
        <div className="mb-24" data-id="element-184">
          <SectionHeader icon={Sparkles} title="Spa & Massage Services" data-id="element-185" />
          <motion.p {...fadeUp} className="text-lavender-100/60 font-light mb-8 -mt-4" data-id="element-186">
            Relax and rejuvenate with our professional treatments.
          </motion.p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-187">
            {spaServices.map((service, index) => <motion.div key={service.title} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.05
          }} className="p-6 rounded-2xl bg-plum-900/40 border border-lavender-400/10 hover:border-lavender-400/40 transition-all duration-300" data-id="element-188">
                <div className="flex justify-between items-baseline gap-4 mb-2" data-id="element-189">
                  <h4 className="font-serif text-xl text-lavender-50" data-id="element-190">
                    {service.title}
                  </h4>
                  <span className="font-sans font-semibold text-xl md:text-2xl text-rose-300 tabular-nums whitespace-nowrap" data-id="element-191">
                    {service.price}
                  </span>
                </div>
                <p className="text-sm text-lavender-100/70 font-light leading-relaxed" data-id="element-192">
                  {service.description}
                </p>
              </motion.div>)}
          </div>
        </div>

        {/* Venue & Add-ons */}
        <div className="mb-24" data-id="element-193">
          <SectionHeader icon={PartyPopper} title="Venue & Hosting" accent="rose" data-id="element-194" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-195">
            {venueServices.map((item, index) => <motion.div key={item.title} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.08
          }} className={`p-6 rounded-2xl border transition-all duration-300 ${item.featured ? 'bg-gradient-to-br from-lavender-500/20 to-rose-500/10 border-lavender-400/40 hover:border-lavender-300/70 md:row-span-1' : 'bg-plum-900/40 border-lavender-400/10 hover:border-lavender-400/40'}`} data-id="element-196">
                <div className="flex justify-between items-baseline gap-4 mb-1" data-id="element-197">
                  <h4 className="font-serif text-xl text-lavender-50" data-id="element-198">
                    {item.title}
                  </h4>
                  <span className="font-sans font-semibold text-xl md:text-2xl text-rose-300 tabular-nums whitespace-nowrap" data-id="element-199">
                    {item.price}
                  </span>
                </div>
                {item.duration && <span className="text-[11px] uppercase tracking-[0.2em] text-lavender-200/50 block mb-3" data-id="element-200">
                    {item.duration}
                  </span>}
                {item.description && <p className="text-sm text-lavender-100/70 font-light leading-relaxed mt-2" data-id="element-201">
                    {item.description}
                  </p>}
              </motion.div>)}
          </div>
        </div>

        {/* Spa Add-Ons */}
        <div className="mb-24" data-id="element-202">
          <SectionHeader icon={Plus} title="Spa Add-Ons" data-id="element-203" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-204">
            {spaAddOns.map((addon, index) => <motion.div key={addon.title} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.05
          }} className="flex items-baseline justify-between gap-4 px-6 py-4 rounded-xl bg-plum-900/30 border border-lavender-400/10 hover:border-lavender-400/30 transition-colors" data-id="element-205">
                <span className="font-light text-lavender-100/90" data-id="element-206">
                  {addon.title}
                </span>
                <span className="font-sans font-semibold text-lg md:text-xl text-rose-300 tabular-nums whitespace-nowrap" data-id="element-207">
                  {addon.price}
                </span>
              </motion.div>)}
          </div>
        </div>

        {/* Refreshments */}
        <div className="mb-12" data-id="element-208">
          <SectionHeader icon={UtensilsCrossed} title="Refreshments" data-id="element-209" />
          <motion.p {...fadeUp} className="text-lavender-100/60 font-light mb-8 -mt-4" data-id="element-210">
            Light bites and beverages for your guests.
          </motion.p>

          {/* Beverages */}
          <div className="mb-10" data-id="element-211">
            <div className="flex items-center gap-2 mb-4" data-id="element-212">
              <Wine size={14} className="text-lavender-300" strokeWidth={1.2} data-id="element-213" />
              <span className="text-[11px] uppercase tracking-[0.2em] text-lavender-200/60" data-id="element-214">
                Beverages
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-215">
              {drinks.map((drink, index) => <motion.div key={drink.title} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.05
            }} className="flex items-center gap-4 p-3 rounded-2xl bg-plum-900/40 border border-lavender-400/10 hover:border-lavender-400/40 transition-all duration-300" data-id="element-216">
                  <div className="w-16 h-16 flex-shrink-0 overflow-hidden rounded-xl" data-id="element-217">
                    <img src={drink.image} alt={drink.title} className="w-full h-full object-cover" data-id="element-218" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-219">
                    <h4 className="font-serif text-base text-lavender-50 truncate" data-id="element-220">
                      {drink.title}
                    </h4>
                    <span className="font-sans font-semibold text-lg text-rose-300 tabular-nums" data-id="element-221">
                      {drink.price}
                    </span>
                  </div>
                </motion.div>)}
            </div>
          </div>

          {/* Food */}
          <div data-id="element-222">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-4" data-id="element-223">
              <div className="flex items-center gap-2" data-id="element-224">
                <UtensilsCrossed size={14} className="text-lavender-300" strokeWidth={1.2} data-id="element-225" />
                <span className="text-[11px] uppercase tracking-[0.2em] text-lavender-200/60" data-id="element-226">
                  Bites & Platters
                </span>
              </div>
              <span className="text-[11px] italic text-lavender-200/50 font-light" data-id="element-227">
                Food items are not exact from pictures but similar.
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-228">
              {food.map((item, index) => <motion.div key={item.title} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.05
            }} className="flex gap-5 p-4 rounded-2xl bg-plum-900/40 border border-lavender-400/10 hover:border-lavender-400/40 transition-all duration-300" data-id="element-229">
                  <div className="w-28 h-28 flex-shrink-0 overflow-hidden rounded-xl" data-id="element-230">
                    <img src={item.image} alt={item.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" data-id="element-231" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-232">
                    <div className="flex justify-between items-baseline gap-3 mb-2" data-id="element-233">
                      <h4 className="font-serif text-lg text-lavender-50" data-id="element-234">
                        {item.title}
                      </h4>
                      <span className="font-sans font-semibold text-xl text-rose-300 tabular-nums whitespace-nowrap" data-id="element-235">
                        {item.price}
                      </span>
                    </div>
                    <p className="text-sm text-lavender-100/70 font-light leading-relaxed" data-id="element-236">
                      {item.description}
                    </p>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </div>

        {/* CTA */}
        <motion.div {...fadeUp} transition={{
        delay: 0.2
      }} className="text-center pt-12" data-id="element-237">
          <a href="#reservations" className="inline-block px-10 py-4 bg-gradient-to-r from-lavender-500 to-rose-500 text-white uppercase tracking-widest text-sm hover:shadow-2xl hover:shadow-lavender-500/40 transition-all duration-300 rounded-full" data-id="element-238">
            Inquire About Your Party
          </a>
        </motion.div>
      </div>
    </section>;
}